import { useCallback, useEffect } from 'react';

import { jwtDecode, useInvoiceDetailStore, useSubscriptionDetailsStore, useSubscriptionPlansStore, useUserAccessStore, useUserDetailsStore } from '@zeta-core';
import { ICurrentSubscriptionResponse } from '@zeta-types';

import { AdvisorAPIs, BillingService, UserServiceAPIs } from '@zeta-services';
import { IUserInfo } from '@zeta-types';
import { useNavigate } from 'react-router';
import { RouterLinks } from '../app-route-paths';
import { isValidToken, setSession, STORAGE_KEY } from './jwt';

// ----------------------------------------------------------------------

type Props = {
  children: React.ReactNode;
};

const FLEX_PLAN_TYPE = 'FLEXIBLE_PLAN';
const FLEX_PLAN_STATUSES = ['Discovery', 'Advice', 'Recommendation', 'Managed', 'Paraplanning', 'Prospect'] as const;

const buildFlexPlanReadOnlyAccess = () => ({
  access: {
    'Advisor-Client-List-View': [{ Type: 'Read', StatusCondition: '' }],
    'Advisor-Client-Summary': FLEX_PLAN_STATUSES.map((status) => ({ Type: 'Read' as const, StatusCondition: status })),
    'Advisor-Client-Task': [{ Type: 'Read', StatusCondition: '' }],
    AdvisorTaskWorkflow: [{ Type: 'Read', StatusCondition: '' }],
    AdvisorMeeting: [{ Type: 'Read', StatusCondition: '' }],
    AiDiscovery: [{ Type: 'Read', StatusCondition: '' }],
    ConversationalDiscovery: [{ Type: 'Read', StatusCondition: '' }],
    FactFindTraceability: [{ Type: 'Read', StatusCondition: '' }],
    HouseholdModeling: FLEX_PLAN_STATUSES.map((status) => ({ Type: 'Read' as const, StatusCondition: status })),
    SecureMessaging: [{ Type: 'Read', StatusCondition: '' }],
    SecureDocumentStorage: FLEX_PLAN_STATUSES.map((status) => ({ Type: 'Read' as const, StatusCondition: status })),
    CaseTracking: [{ Type: 'Read', StatusCondition: '' }],
  },
});

const hasAccessFeatures = (payload: { access?: Record<string, unknown> } | null | undefined) => Object.keys(payload?.access || {}).length > 0;

const mergeFlexPlanReadOnlyAccess = (payload: { access?: Record<string, Array<{ Type?: string; StatusCondition?: string }>> } | null | undefined) => {
  const fallback = buildFlexPlanReadOnlyAccess();
  const mergedAccess = { ...(payload?.access || {}) } as Record<string, Array<{ Type?: string; StatusCondition?: string }>>;

  Object.entries(fallback.access).forEach(([feature, fallbackPermissions]) => {
    const existingPermissions = mergedAccess[feature] || [];
    const seen = new Set(existingPermissions.map((permission) => `${permission?.Type || ''}:${permission?.StatusCondition || ''}`));

    mergedAccess[feature] = [
      ...existingPermissions,
      ...fallbackPermissions.filter((permission) => {
        const key = `${permission?.Type || ''}:${permission?.StatusCondition || ''}`;
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      }),
    ];
  });

  return {
    ...(payload || {}),
    access: mergedAccess,
  };
};

const isFlexibleSubscriptionPlan = (subscription: ICurrentSubscriptionResponse | null, plans: Array<{ planId?: string; planType?: string }>) => {
  const planReferenceId = subscription?.assignedPlanReferenceID || subscription?.plans?.[0]?.licenceReferenceID;
  return plans.some((plan) => plan.planId === planReferenceId && plan.planType === FLEX_PLAN_TYPE);
};

export function AuthGuard({ children }: Props) {
  const navigate = useNavigate();
  const updateUser = useUserDetailsStore((state) => state.updateUser);
  const updateSubscriptionPlans = useSubscriptionPlansStore((state) => state.updateSubscriptionPlans);
  const updateSubscriptionPlansLoading = useSubscriptionPlansStore((state) => state.updateLoading);
  const subscriptionSuspended = useInvoiceDetailStore((state) => state.suspended);
  const updateSubscription = useSubscriptionDetailsStore((state) => state.updateSubscription);
  const updateSubscriptionLoading = useSubscriptionDetailsStore((state) => state.updateLoading);
  const updateInvoiceDetail = useInvoiceDetailStore((state) => state.updateInvoiceDetail);
  const updateUserAccess = useUserAccessStore((state) => state.updateUserAccess);
  const updateUseraccessLoading = useUserAccessStore((state) => state.updateLoading);

  const checkUserSession = useCallback(async () => {
    try {
      const accessToken = sessionStorage.getItem(STORAGE_KEY);

      if (accessToken && isValidToken(accessToken)) {
        setSession(accessToken);
        const decodedToken = jwtDecode(accessToken);
        if (decodedToken?.username) {
          updateUseraccessLoading(true);

          // Set subscription loading to true before fetching
          updateSubscriptionLoading(true);
          updateSubscriptionPlansLoading(true);

          // Fetch user details and subscription data sequentially to avoid race condition
          (async () => {
            let hasResolvedUserAccess = false;
            let currentSubscriptionData: ICurrentSubscriptionResponse | null = null;
            let resolvedPlans: Array<{ planId?: string; planType?: string }> = [];
            let resolvedUserAccess: { access?: Record<string, Array<{ Type?: string; StatusCondition?: string }>> } | null = null;

            try {
              try {
                const userAccessFeaturesRes = await UserServiceAPIs().getUserAccessFeatures({});
                if (hasAccessFeatures(userAccessFeaturesRes.data)) {
                  resolvedUserAccess = userAccessFeaturesRes.data;
                  updateUserAccess(userAccessFeaturesRes.data);
                  hasResolvedUserAccess = true;
                }
              } catch (error) {
                console.error('Error fetching user access features:', error);
              }

              // Fetch user details first
              const userResponse = await UserServiceAPIs().userDetails({ login: true });
              updateUser(userResponse.data?.responseItems?.[0] as IUserInfo);
              const advisorId = userResponse.data?.responseItems?.[0]?.accessibleEntityID;

              // Fetch subscription plans (static data, always fetch regardless of subscription status)
              if (advisorId) {
                try {
                  const subscriptionPlansRes = await BillingService().getSubscriptionPlans({ advisorId });
                  // The API returns an array of plans in the data field
                  const plans = Array.isArray(subscriptionPlansRes.data)
                    ? subscriptionPlansRes.data
                    : [subscriptionPlansRes.data];
                  resolvedPlans = plans;
                  updateSubscriptionPlans(plans);
                } catch (plansError) {
                  console.error('Error fetching subscription plans:', plansError);
                  resolvedPlans = [];
                  updateSubscriptionPlans([]);
                }
              } else {
                // No advisorId, set empty array
                resolvedPlans = [];
                updateSubscriptionPlans([]);
              }

              // Fetch subscription
              try {
                const currentSubscriptionRes = await AdvisorAPIs().getCurrentSubscription({});
                updateSubscription(currentSubscriptionRes.data);
                currentSubscriptionData = currentSubscriptionRes.data;

                // Fetch invoices
                try {
                  const invoiceDetailRes = await AdvisorAPIs().fetchInvoices({});
                  updateInvoiceDetail({
                    invoices: invoiceDetailRes.data?.invoices || [],
                    suspended: currentSubscriptionData?.cancelStatus === 'SUSPENDEDTOCANCEL' ? true : false,
                  });
                } catch (invoiceError) {
                  console.error('Error fetching invoice detail:', invoiceError);
                }
              } catch (error: any) {
                if (error?.response?.data?.error?.errorCode === 'A030002') {
                  updateSubscription(null);
                  currentSubscriptionData = null;
                  console.log('error', error?.response?.data?.error?.errorCode);
                  // navigate(RouterLinks.pricing);
                } else {
                  console.error('Error fetching subscription:', error);
                }
              }

              if (advisorId) {
                const isCurrentPlanFlexible = isFlexibleSubscriptionPlan(currentSubscriptionData, resolvedPlans);

                let hasFlexibleFallbackAccess = false;
                if (!isCurrentPlanFlexible) {
                  const [planAssignmentsResult, flexiblePlanStatusResult] = await Promise.allSettled([
                    AdvisorAPIs().getAdvisorPlanAssignments({ advisorID: advisorId }),
                    BillingService().getFlexiblePlanStatus({ advisorId }),
                  ]);

                  const hasFlexiblePlanAssignment =
                    planAssignmentsResult.status === 'fulfilled'
                      ? (planAssignmentsResult.value.data?.plans || []).some((plan: { planName?: string }) => {
                          const planName = (plan?.planName || '').toLowerCase();
                          return planName.includes('flex plan') || planName.includes('flexible');
                        })
                      : false;

                  const hasActiveFlexiblePlan =
                    flexiblePlanStatusResult.status === 'fulfilled' ? flexiblePlanStatusResult.value.data?.active === true : false;

                  hasFlexibleFallbackAccess = !currentSubscriptionData && (hasFlexiblePlanAssignment || hasActiveFlexiblePlan);
                }

                if (isCurrentPlanFlexible || hasFlexibleFallbackAccess) {
                  updateUserAccess(mergeFlexPlanReadOnlyAccess(resolvedUserAccess));
                  hasResolvedUserAccess = true;
                }
              }
            } catch (error) {
              console.error('Error fetching user details:', error);
              updateSubscriptionPlans([]);
            } finally {
              updateUseraccessLoading(false);
              updateSubscriptionLoading(false);
              updateSubscriptionPlansLoading(false);
            }
          })();
        }
      } else {
        navigate(RouterLinks.login);
      }
    } catch (error) {
      console.error(error);
    }
  }, [navigate, updateUser]);

  useEffect(() => {
    checkUserSession();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return children;
}
